import { useSetPaymentMethod } from '@/contexts/PaymentMethods';
import { useTagadaContext } from '@tagadapay/plugin-sdk/v2';
import { memo, useEffect, useState } from 'react';
import { PAYMENT_TYPES } from './payment-types';

interface TabbyCheckoutProps {
    checkoutSessionId: string;
    storeId: string;
    amount?: number;
    currency?: string;
    buyerEmail?: string;
    buyerPhone?: string;
    buyerName?: string;
}

/**
 * TabbyCheckout is a thin component responsible for:
 * 1. Calling the eligibility check endpoint on mount.
 * 2. Updating `setPaymentMethod({ isValid })` based on eligibility.
 * 3. Showing a rejection message when the customer is not eligible.
 *
 * It does NOT handle the redirect — that is done in SingleStepCheckout's
 * onSubmit handler by calling POST /tabby-payment and redirecting to forwardUrl.
 */
const TabbyCheckout = memo(({
    checkoutSessionId,
    storeId,
    amount,
    currency,
    buyerEmail,
    buyerPhone,
    buyerName,
}: TabbyCheckoutProps) => {
    const setPaymentMethod = useSetPaymentMethod();
    const { apiService } = useTagadaContext();
    const [isChecking, setIsChecking] = useState(false);
    const [rejectionReason, setRejectionReason] = useState<string | null>(null);
    const [eligible, setEligible] = useState<boolean | null>(null);

    useEffect(() => {
        let cancelled = false;

        async function checkEligibility() {
            setIsChecking(true);
            try {
                const result = await apiService.fetch<{ eligible: boolean; rejectionReason: string | null }>(
                    `/api/v1/checkout-sessions/${checkoutSessionId}/tabby-eligibility`,
                    {
                        method: 'POST',
                        headers: { 'content-type': 'application/json' },
                        body: JSON.stringify({
                            storeId,
                            buyerEmail: buyerEmail ?? '',
                            buyerPhone: buyerPhone ?? '',
                            buyerName: buyerName ?? '',
                        }),
                    },
                );

                if (cancelled) return;

                const isEligible = result?.eligible ?? true;
                const reason = result?.rejectionReason ?? null;

                setEligible(isEligible);
                setRejectionReason(reason);

                setPaymentMethod({
                    isValid: isEligible,
                    type: PAYMENT_TYPES.TABBY,
                    informations: null,
                });
            } catch {
                if (cancelled) return;
                // Fail open — let the user try
                setEligible(true);
                setPaymentMethod({
                    isValid: true,
                    type: PAYMENT_TYPES.TABBY,
                    informations: null,
                });
            } finally {
                if (!cancelled) setIsChecking(false);
            }
        }

        checkEligibility();
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checkoutSessionId, storeId, buyerEmail, buyerPhone, buyerName]);

    if (isChecking) {
        return (
            <div className="px-4 pb-3">
                <p className="text-xs text-muted-foreground animate-pulse">
                    Checking Tabby eligibility…
                </p>
            </div>
        );
    }

    if (eligible === false) {
        return (
            <div className="px-4 pb-3">
                <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2">
                    <p className="text-xs text-red-700">
                        {rejectionReason
                            ? `Tabby is not available for your order: ${rejectionReason}`
                            : 'Tabby is not available for your order at this time.'}
                    </p>
                </div>
            </div>
        );
    }

    // Eligible — no extra UI needed (radio row already shows the Tabby option)
    return null;
});

TabbyCheckout.displayName = 'TabbyCheckout';

export { TabbyCheckout };
